import { UserRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import { defaultOrgName } from '@/features/org/utils/default-org-name';
import { PartnerPopulated } from '@/features/partners/api/get-partners';
import { getProjectRoleDisplay } from '@/features/projects/shared/types/project-role';
import { CustomTable } from '@/shared/components/table/table';
import { CustomTableHeader } from '@/shared/components/table/table-header';
import {
  CustomTableRow,
  TableRowProps,
} from '@/shared/components/table/table-row';
import { TableBody } from '@/shared/components/ui/table';
import { useProjectHubFilterStore } from '@/shared/hooks/use-hub-filter-store';

type PartnerTableProps = {
  onEdit: (partner: PartnerPopulated) => void;
  partners: PartnerPopulated[];
};

export const PartnerTable = ({ onEdit, partners }: PartnerTableProps) => {
  const { t } = useTranslation('partner', { keyPrefix: 'PartnerHub' });
  const { t: tProjectRole } = useTranslation('shared', {
    keyPrefix: 'ProjectRole',
  });
  const navigate = useNavigate();
  const { setPartnerIds } = useProjectHubFilterStore();

  const TableColumns = [
    t('TableColumns.partner'),
    t('TableColumns.alias'),
    t('TableColumns.role'),
    t('TableColumns.projects_count'),
    t('TableColumns.partner_since'),
  ];

  const columnWidths = [
    'w-[264px]', // Partner
    'w-[204px]', // Alias
    'w-[242px]', // Role
    'w-[256px]', // Projects count
    'min-w-[250px] flex-1', // Partner since
  ];

  const tablRows: TableRowProps[] = partners
    ? partners.map(
        (partner): TableRowProps => ({
          type: 'single',
          actions: [
            {
              action: (
                <div className="flex flex-row gap-1">
                  <UserRound /> {t('change_alias_title')}
                </div>
              ),
              onClick: () => onEdit(partner),
            },
          ],
          showMenu: true,
          props: [
            {
              value: {
                text: partner.organization.name ?? defaultOrgName,
                type: 'string',
              },
              width: columnWidths[0] || 'flex-1',
            },
            {
              value: {
                text:
                  partner.alias && partner.alias.trim() !== ''
                    ? partner.alias
                    : '-',
                type: 'string',
              },
              width: columnWidths[1] || 'flex-1',
            },
            {
              value: {
                text: partner.partner_role
                  ? getProjectRoleDisplay(partner.partner_role, tProjectRole)
                  : '-',
                type: 'string',
              },
              width: columnWidths[2] || 'flex-1',
            },
            {
              value: { number: partner.active_projects, type: 'number' },
              width: columnWidths[3] || 'flex-1',
            },
            {
              value: {
                type: 'date',
                date: partner.created_at ? new Date(partner.created_at) : null,
              },
              width: columnWidths[4] || 'flex-1',
            },
          ],
        }),
      )
    : [];

  return (
    <CustomTable>
      <CustomTableHeader columns={TableColumns} columnWidths={columnWidths} />
      <TableBody>
        {tablRows.map((row, index) => {
          const partner = partners[index];
          return (
            <CustomTableRow
              key={index}
              props={row.props}
              type="single"
              showMenu={row.showMenu}
              actions={row.actions}
              isFirstRow={index === 0}
              isLastRow={index === tablRows.length - 1}
              onClick={() => {
                setPartnerIds([partner.id]);
                navigate(paths.projects.hub.path);
              }}
            />
          );
        })}
      </TableBody>
    </CustomTable>
  );
};
